import { useTranslation } from 'react-i18next';
import './ManualDataUploader.scss';
import { ReactComponent as UploadIcon } from '../../icons/upload_arrow.svg';
import { ReactComponent as SendIcon } from '../../icons/sent.svg';
import FileUploader from '../FileUploader/FileUploader';
import { UploadedFile } from '../../core/types';
import MiniLoader from '../MiniLoader/MiniLoader';
import ManualUploadFile from './ManualUploadFile';
import { formatDate } from '../../core/helpers';
import { endOfMonth, startOfMonth, subMonths } from 'date-fns';
import {
  ACCEPTED_EXTS,
  ManualUploadFileStatusEnum,
} from '../../context/useManualUpload';
import ManualUploadFileLinkV2 from './ManualUploadFileLinkV2';
import { useModal } from '../Modal/useModal';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import { getStopMailing, postStopMailing } from '../../core/api';
import { DEFAULT_ERROR } from '../Notifications/Notification';
import { addNotification } from '../../core/store/notificationsSlice';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../hooks';
import { useMU } from '../../context/mu-context';
import { useQuery, useQueryClient } from 'react-query';
import { Loader } from '../Loader/Loader';
import Button from '../Button';

export default function ManualDataUploader() {
  const {
    onSubmitMU,
    onSelectFilesMU,
    onDeleteFileMU,
    filesToUploadMU,
    isUploadingMU,
    uploadedFilesMU,
  } = useMU();

  const queryClient = useQueryClient();

  const user = useAppSelector((s) => s.user.user);
  const activeCompany = useAppSelector((s) => s.user.activeCompany);

  const manualUploadMailingStopped = useQuery({
    queryKey: [
      'SellerStopManualUploadMailing',
      user!.Id,
      activeCompany?.company_id,
    ],
    queryFn: () =>
      getStopMailing('manual-upload-needed', activeCompany?.company_id),
  });

  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const activeCompanyId = useAppSelector(
    (s) => s.user.activeCompany!.company_id
  );

  const date = subMonths(new Date(), 1);
  const deadlineDate = new Date();
  deadlineDate.setDate(10);

  const { isModalOpened, closeModal, openModal } = useModal();

  const hasProcessedFiles = uploadedFilesMU.some(
    (f) => f.status !== ManualUploadFileStatusEnum.CREATED && !f.errors.length
  );

  const isAlertShown =
    activeCompany?.ManualUploadNeeded &&
    !hasProcessedFiles &&
    !manualUploadMailingStopped.data;

  return (
    <div className='Paper pt-3 pb-4 px-4 mb-3'>
      {manualUploadMailingStopped.isLoading && <Loader />}
      {isAlertShown && (
        <div
          style={{
            lineHeight: 1,
          }}
        >
          <div className='PurchasePricesUploader__badge text-uppercase px-1 Text--sm text-white rounded d-inline-block py-0 m-0'>
            {t('Until')}&nbsp;
            {i18n.language === 'de'
              ? formatDate(deadlineDate, 'do MMMM')
              : formatDate(deadlineDate, 'do MMMM').replace('th', 'th of')}
          </div>
        </div>
      )}
      <div className='Text--30 fw-900 my-2'>{t('Upload data file')}</div>
      {!manualUploadMailingStopped.data ? (
        <>
          <div className='mb-2 multiline'>
            {t(
              'Upload your data files for *month*. You are able to upload multiple files.'
            ).replace('*month*', formatDate(date, 'MMMM yyyy'))}
          </div>
          <div className='mb-2'>
            <span className='me-2 fw-800'>{t('Period in processing')}</span>
            <span>{formatDate(startOfMonth(date), 'dd.MM.yyyy')}—</span>
            <span>{formatDate(endOfMonth(date), 'dd.MM.yyyy')}</span>
          </div>
        </>
      ) : (
        <div>
          <p className='mb-2'>
            {t(
              'countX will submit zero VAT return for *month*. You have confirmed that there is no manual turnover data.'
            ).replace('*month*', formatDate(date, 'MMMM yyyy'))}
          </p>
          {t('You still can upload data for past periods.')}
        </div>
      )}

      <form onSubmit={onSubmitMU}>
        {!!filesToUploadMU.length && (
          <div className='border-top pt-2'>
            {filesToUploadMU.map((f) => (
              <ManualUploadFile
                className='mb-3'
                onDeleteFile={onDeleteFileMU(f.name)}
                file={f}
                disabled={false}
                key={f.name}
                isUploading={isUploadingMU}
              />
            ))}
          </div>
        )}
        <div className='row row-cols-auto gx-3 pt-3'>
          {!isUploadingMU && (
            <div className='col'>
              <FileUploader
                onSelect={onSelectFilesMU}
                name='manual-upload-file'
                accept={ACCEPTED_EXTS}
                className='cursor-pointer Button--outline ps-3 pe-4 py-2 d-flex align-items-center'
              >
                <>
                  <UploadIcon className='me-2' />
                  <span>{t('Select file')}</span>
                </>
              </FileUploader>
            </div>
          )}
          {!!filesToUploadMU.length && (
            <div className='col'>
              <Button
                className='py-2 ps-3 pe-4 d-flex align-items-center'
                type='submit'
                disabled={isUploadingMU}
                data-testid='manual-upload-submit'
              >
                {isUploadingMU ? (
                  <MiniLoader className='MiniLoader--sm d-flex align-items-center justify-content-center me-2 MiniLoader--gray' />
                ) : (
                  <SendIcon className='me-2' />
                )}
                <span>{t('Submit')}</span>
              </Button>
            </div>
          )}
        </div>
      </form>
      <>
        {!!uploadedFilesMU.length && (
          <div className='mb-n3'>
            <div className='fw-800 mb-2 mt-4'>{t('Submitted files')}</div>
            {uploadedFilesMU
              .sort((a, b) => {
                return (
                  new Date(b.imported_at).getTime() -
                  new Date(a.imported_at).getTime()
                );
              })
              .map((f) => (
                <ManualUploadFileLinkV2
                  file={f}
                  key={f.name}
                  collapsed={hasProcessedFiles}
                />
              ))}
          </div>
        )}
        <div className='mt-4'>
          <span>
            {t("If you don't have manual turnover data in this month")}&nbsp;
          </span>
          <Button onClick={openModal} styling='naked-blue'>
            {t('click here')}
          </Button>
        </div>
        <ConfirmModal
          isModalOpened={isModalOpened}
          closeModal={closeModal}
          confirm={(setIsLoading) => {
            setIsLoading(true);
            postStopMailing('manual-upload-needed', activeCompanyId)
              .then(() => {
                queryClient.invalidateQueries([
                  'SellerStopManualUploadMailing',
                  user!.Id,
                  activeCompany?.company_id,
                ]);
                setIsLoading(false);
                closeModal();
                dispatch(
                  addNotification({
                    text: t('No manual turnover data confirmed'),
                    type: 'success',
                  })
                );
              })
              .catch(() => {
                dispatch(
                  addNotification({
                    text: t(DEFAULT_ERROR),
                    type: 'error',
                  })
                );
                setIsLoading(false);
              });
          }}
          confirmBtnText='Yes, I confirm'
          title='Confirm action'
        >
          <>
            <div className='Text fw-900 mb-2'>
              {t(
                'Please confirm that there is no manual turnover data for *month*'
              ).replace('*month*', formatDate(date, 'MMMM yyyy'))}
            </div>
            <div className='mb-3'>
              {t(
                'In this case we will submit zero VAT return and you will not receive any email reminders in the current month.'
              )}
            </div>
          </>
        </ConfirmModal>
      </>
    </div>
  );
}

export const sortUpoadledFiles = (a: UploadedFile, b: UploadedFile) => {
  if (a.imported_at && !b.imported_at) {
    return 1;
  }

  if (b.imported_at && !a.imported_at) {
    return -1;
  }

  if (b.imported_at && a.imported_at) {
    return (
      new Date(a.imported_at).getTime() - new Date(b.imported_at).getTime()
    );
  }

  return 0;
};
